/**
 * A field key rename, carried into the document (R5).
 *
 * The document — placements on its PDFs, the repeating regions, the AcroForm
 * mappings, the parts list and the questions an answers part hides — names
 * fields by key, exactly as rules and
 * widgets do. `renameFieldKey` in builderModel has always moved rules and
 * widgets with a renamed field; the document was left behind, and a placement
 * pointing at a key the form no longer had printed nothing, silently, on the
 * first submission after publishing. This is the missing half.
 *
 * Pure, and mirrored on the server (`server/src/lib/documentRename.ts`, kept
 * in step by `S/documentRename.test.ts`): the document saves LIVE and never
 * rides the draft, so a rename made in the builder is recorded on the working
 * copy (`FormDraft.renames`) and applied to the stored document by the
 * publish route, in the same transaction that makes the new key live. The
 * client applies the same renames to what it SHOWS — the Outline's p-tags,
 * the placed-answers list — so the Document tab reads the draft's names while
 * the stored document still holds the live ones.
 *
 * Composite sub-paths ('shipTo.city') follow their parent, and `{{key}}`
 * tokens in headings, paragraphs and the file name follow too (whole tokens
 * only: `{{company}}` moves, `{{companyName}}` does not).
 */

export interface KeyRename { from: string; to: string }

/** A document as the settings hold it — read loosely: it is stored JSON. */
export type DocumentLike = Record<string, unknown>;

/** `k` after one rename: the key itself, or a sub-path under it. */
export function renameKey(k: string, from: string, to: string): string {
  if (k === from) return to;
  return k.startsWith(`${from}.`) ? to + k.slice(from.length) : k;
}

/** `{{key}}`, `{{key.part}}`, `{{#if key}}`, `{{#each key}}`, `{{/key}}` — the whole token, never a prefix of another. */
export function renameTokens(text: string, from: string, to: string): string {
  if (!text || !text.includes(from)) return text;
  const esc = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`(\\{\\{\\s*(?:#if\\s+|#each\\s+|/)?)${esc}(?=\\s*\\}\\}|\\.)`, 'g'), `$1${to}`);
}

/** One rename applied to one stored document. Returns the same object when nothing named `from`. */
export function renameKeyInDocument(doc: DocumentLike, from: string, to: string): DocumentLike {
  if (!doc || typeof doc !== 'object' || !from || !to || from === to) return doc;
  let changed = false;
  const at = (k: unknown): unknown => {
    if (typeof k !== 'string') return k;
    const next = renameKey(k, from, to);
    if (next !== k) changed = true;
    return next;
  };
  const text = (t: unknown): unknown => {
    if (typeof t !== 'string') return t;
    const next = renameTokens(t, from, to);
    if (next !== t) changed = true;
    return next;
  };
  const out: DocumentLike = { ...doc };

  if (typeof doc.filename === 'string') out.filename = text(doc.filename);

  // Every PDF — the one `template` of a document stored before a form could
  // have several, and each of `templates` since.
  const inTemplate = (template: unknown): unknown => {
    if (!template || typeof template !== 'object') return template;
    const t: DocumentLike = { ...(template as DocumentLike) };
    if (t.acroMap && typeof t.acroMap === 'object' && !Array.isArray(t.acroMap)) {
      const map: Record<string, unknown> = {};
      for (const [name, key] of Object.entries(t.acroMap as Record<string, unknown>)) map[name] = at(key);
      t.acroMap = map;
    }
    if (Array.isArray(t.placements)) {
      t.placements = t.placements.map((p) => (p && typeof p === 'object' ? { ...(p as DocumentLike), fieldKey: at((p as DocumentLike).fieldKey) } : p));
    }
    if (Array.isArray(t.regions)) {
      t.regions = t.regions.map((r) => {
        if (!r || typeof r !== 'object') return r;
        const region = { ...(r as DocumentLike) };
        if (Array.isArray(region.columns)) {
          region.columns = region.columns.map((c) => (c && typeof c === 'object' ? { ...(c as DocumentLike), fieldKey: at((c as DocumentLike).fieldKey) } : c));
        }
        const total = region.total as DocumentLike | undefined;
        if (total && typeof total === 'object') region.total = { ...total, column: at(total.column) };
        return region;
      });
    }
    return t;
  };
  if (doc.template && typeof doc.template === 'object') out.template = inTemplate(doc.template);
  if (Array.isArray(doc.templates)) out.templates = doc.templates.map(inTemplate);

  if (Array.isArray(doc.blocks)) {
    out.blocks = doc.blocks.map((b) => {
      if (!b || typeof b !== 'object') return b;
      const block = { ...(b as DocumentLike) };
      switch (block.type) {
        case 'terms':
        case 'signature':
          block.fieldKey = at(block.fieldKey);
          break;
        case 'answers':
          if (Array.isArray(block.hide)) block.hide = block.hide.map(at);
          break;
        case 'heading':
        case 'paragraph':
          block.text = text(block.text);
          break;
        case 'table': {
          if (Array.isArray(block.columns)) {
            block.columns = block.columns.map((c) => (c && typeof c === 'object' ? { ...(c as DocumentLike), fieldKey: at((c as DocumentLike).fieldKey) } : c));
          }
          const total = block.total as DocumentLike | undefined;
          if (total && typeof total === 'object') block.total = { ...total, column: at(total.column) };
          break;
        }
        default: break;
      }
      return block;
    });
  }

  return changed ? out : doc;
}

/**
 * Every recorded rename, in the order they were made. Sequential on purpose:
 * `a → b` then `b → c` lands on `c`, and `a → b` then `b → a` lands back on
 * `a`, without this function having to know either story.
 */
export function renameKeysInDocument(doc: DocumentLike | undefined, renames: KeyRename[] | undefined): DocumentLike | undefined {
  if (!doc || !renames?.length) return doc;
  let out = doc;
  for (const r of renames) {
    if (!r || typeof r.from !== 'string' || typeof r.to !== 'string') continue;
    out = renameKeyInDocument(out, r.from, r.to);
  }
  return out;
}

/** The key a draft field has NOW for a key the live form has — the renames, forward. */
export function draftKeyOf(liveKey: string, renames: KeyRename[] | undefined): string {
  let k = liveKey;
  for (const r of renames || []) k = renameKey(k, r.from, r.to);
  return k;
}

/** The key the live form has for a draft field's key — the renames, backwards. */
export function liveKeyOf(draftKey: string, renames: KeyRename[] | undefined): string {
  let k = draftKey;
  for (let i = (renames || []).length - 1; i >= 0; i -= 1) {
    const r = renames![i];
    k = renameKey(k, r.to, r.from);
  }
  return k;
}

/**
 * Record one more rename on the working copy, folded where it can be: a
 * rename straight back (`a → b`, then `b → a`) cancels, and a chain
 * (`a → b`, then `b → c`) collapses to `a → c`. Both keep the list short and
 * keep "what will publishing do to the document" readable.
 */
export function recordRename(renames: KeyRename[] | undefined, from: string, to: string): KeyRename[] {
  const list = (renames || []).slice();
  if (!from || !to || from === to) return list;
  const last = list[list.length - 1];
  if (last && last.to === from) {
    list.pop();
    if (last.from !== to) list.push({ from: last.from, to });
    return list;
  }
  list.push({ from, to });
  return list;
}
